import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Spinner, Form, Badge, Alert } from 'react-bootstrap';
import useToken from 'hooks/useToken';
import FalconCloseButton from 'components/common/FalconCloseButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useToast } from 'context/providers/ToastProvider';

const ModalNuevaSuscripcion = ({
    show,
    setShow,
    idCliente,
    onSuscripcionCreada
}) => {
    const { token } = useToken();
    const { showErrorToast, showSuccessToast } = useToast();

    // Estados principales
    const [productos, setProductos] = useState([]);
    const [productoSeleccionado, setProductoSeleccionado] = useState(null);
    const [renovaciones, setRenovaciones] = useState([]);
    const [renovacionSeleccionada, setRenovacionSeleccionada] = useState(null);
    const [nombreSuscripcion, setNombreSuscripcion] = useState('');
    const [monedaSeleccionada, setMonedaSeleccionada] = useState('MXN');
    const [cargando, setCargando] = useState(false);

    // 🔧 Estados para licencias
    const [tiposLicencia, setTiposLicencia] = useState([]);
    const [tipoLicenciaSeleccionado, setTipoLicenciaSeleccionado] = useState(null);
    const [licenciasServidor, setLicenciasServidor] = useState([]);
    const [licenciaServidorVinculada, setLicenciaServidorVinculada] = useState(null);
    const [cargandoLicenciasServidor, setCargandoLicenciasServidor] = useState(false);
    const [vUUIDLicencia, setVUUIDLicencia] = useState('');
    const [vUUIDEmpresa, setVUUIDEmpresa] = useState('');
    const [vLatLon, setVLatLon] = useState('');

    // 🆕 NUEVO: Estado para días de periodo de prueba
    const [iDiasPeriodoPrueba, setIDiasPeriodoPrueba] = useState(5); // Default: 5 días

    /**
     * 🆕 Mapea el tipo de renovación al idCatCicloFacturacion correspondiente
     * 1 = MENSUAL
     * 2 = SEMESTRAL
     * 3 = ANUAL
 * 4 = PERMANENTE
     */
    const obtenerIdCicloFacturacion = (tipoRenovacion) => {
        const mapeo = {
            'mensual': 1,
            'semestral': 2,
            'anual': 3,
            'permanente': 4
        };
        return mapeo[tipoRenovacion.toLowerCase()] || 1; // Default: Mensual
    };

    const handleClose = () => {
        // Limpiar todos los estados
        setProductoSeleccionado(null);
        setRenovaciones([]);
        setRenovacionSeleccionada(null);
        setNombreSuscripcion('');
        setMonedaSeleccionada('MXN');
        setTipoLicenciaSeleccionado(null);
        setLicenciaServidorVinculada(null);
        setVUUIDLicencia('');
        setVUUIDEmpresa('');
        setVLatLon('');
        setIDiasPeriodoPrueba(5); // 🆕 Resetear días de prueba
        setShow(false);
    };

    /**
     * 🔧 Obtiene los tipos de licencia del catálogo
   */
    const obtenerTiposLicencia = async () => {
        try {
            const response = await fetch('/api/Suscripciones/ObtenerTipoLicencias', {
                method: 'GET',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                // El SP ahora devuelve el patrón estándar con table1 (respuesta) y table2 (datos)
                if (data.table1 && data.table1.length > 0) {
                    const respuesta = data.table1[0];
                    if (respuesta.iResponseType === 1) {
                        if (data.table2 && data.table2.length > 0) {
                            setTiposLicencia(data.table2);
                            console.log('? Tipos de licencia cargados:', data.table2.length);
                        }
                    } else {
                        console.error('Error del SP:', respuesta.vMessage);
                        showErrorToast(respuesta.vMessage);
                    }
                }
            } else {
                console.error('Error al obtener tipos de licencia:', response.status);
                showErrorToast('Error al obtener tipos de licencia: ' + response.status);
            }
        } catch (error) {
            console.error('Error al obtener tipos de licencia:', error);
            showErrorToast('Error al obtener tipos de licencia: ' + error.message);
        }
    };

    /**
     * 🔧 Obtiene las licencias tipo Servidor del cliente
  */
    const obtenerLicenciasServidor = async () => {
        if (!idCliente) return;

        setCargandoLicenciasServidor(true);
        try {
            const response = await fetch(`/api/Suscripciones/GetLicenciasByCliente?idCliente=${idCliente}`, {
                method: 'GET',
                headers:
                    {
                        'Authorization': 'Bearer ' + token,
                        'Content-Type': 'application/json'
                    }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.table2 && data.table2.length > 0) {
                    // Filtrar solo licencias tipo Servidor (idCatTipoLicencia = 1)
                    const licenciasServidorFiltradas = data.table2.filter(
                        lic => lic.idCatTipoLicencia === 1
                    );
                    setLicenciasServidor(licenciasServidorFiltradas);
                } else {
                    setLicenciasServidor([]);
                }
            }
        } catch (error) {
            console.error('Error al obtener licencias servidor:', error);
            setLicenciasServidor([]);
        } finally {
            setCargandoLicenciasServidor(false);
        }
    };

    /**
 * 🔧 Genera UUIDs aleatorios para la licencia
     */
    const generarUUIDs = async () => {
        try {
            // Generar UUID Licencia
            const response1 = await fetch('/api/Suscripciones/ObtenerUUIDLicenciaRandom', {
                method: 'GET',
                headers:
                    {
                        'Authorization': 'Bearer ' + token
                    }
            });
            if (response1.ok) {
                let uuid1 = await response1.text();
                // Limpiar cualquier HTML o espacios en blanco
                uuid1 = uuid1.replace(/<[^>]*>/g, '').trim();
                setVUUIDLicencia(uuid1);
                console.log('? UUID Licencia generado:', uuid1);
            }

            // Generar UUID Empresa
            const response2 = await fetch('/api/Suscripciones/ObtenerUUIDEmpresaRandom', {
                method: 'GET',
                headers:
                    {
                        'Authorization': 'Bearer ' + token
                    }
            });
            if (response2.ok) {
                let uuid2 = await response2.text();
                // Limpiar cualquier HTML o espacios en blanco
                uuid2 = uuid2.replace(/<[^>]*>/g, '').trim();
                setVUUIDEmpresa(uuid2);
                console.log('? UUID Empresa generado:', uuid2);
            }
        } catch (error) {
            console.error('Error al generar UUIDs:', error);
            showErrorToast('Error al generar UUIDs de licencia');
        }
    };

    /**
* 🔧 Regenera solo el UUID de Licencia
     */
    const regenerarUUIDLicencia = async () => {
        try {
            const response = await fetch('/api/Suscripciones/ObtenerUUIDLicenciaRandom', {
                method: 'GET',
                headers:
                    {
                        'Authorization': 'Bearer ' + token
                    }
            });
            if (response.ok) {
                let uuid = await response.text();
                uuid = uuid.replace(/<[^>]*>/g, '').trim();
                setVUUIDLicencia(uuid);
                console.log('? UUID Licencia regenerado:', uuid);
            }
        } catch (error) {
            console.error('Error al regenerar UUID Licencia:', error);
            showErrorToast('Error al regenerar UUID de licencia');
        }
    };

    /**
  * 🔧 Regenera solo el UUID de Empresa
     */
    const regenerarUUIDEmpresa = async () => {
        try {
            const response = await fetch('/api/Suscripciones/ObtenerUUIDEmpresaRandom', {
                method: 'GET',
                headers:
                    {
                        'Authorization': 'Bearer ' + token
                    }
            });
            if (response.ok) {
                let uuid = await response.text();
                uuid = uuid.replace(/<[^>]*>/g, '').trim();
                setVUUIDEmpresa(uuid);
                console.log('? UUID Empresa regenerado:', uuid);
            }
        } catch (error) {
            console.error('Error al regenerar UUID Empresa:', error);
            showErrorToast('Error al regenerar UUID de empresa');
        }
    };

    const obtenerProductos = async () => {
        try {
            const response = await fetch('/api/Suscripciones/ObtenerProductosRenovacion', {
                method: 'GET',
                headers:
                    {
                        'Authorization': 'Bearer ' + token,
                        'Content-Type': 'application/json'
                    }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.iResponseType === 1) {
                    const parsed = JSON.parse(data.detalles);
                    setProductos(parsed.table2 || []);
                } else {

                    showErrorToast(data.vMessage);
                }
            } else {

                showErrorToast(response.status + ' ' + response.statusText);
            }
        } catch (error) {

            showErrorToast('Hubo un problema con la petición: ' + error.message);
        }
    };

    useEffect(() => {
        if (show) {
            obtenerProductos();
            obtenerTiposLicencia();
            obtenerLicenciasServidor();
        }
    }, [show, idCliente]);

    // 🔧 Generar UUIDs cuando se selecciona un producto tipo Licencia
    useEffect(() => {
        if (productoSeleccionado && productoSeleccionado.vTipoProducto === 'Licencia') {
            generarUUIDs();
        }
    }, [productoSeleccionado]);

    const productosFiltrados = productos.filter(p => p.vMoneda === monedaSeleccionada);

    const onSelectProducto = async (prod) => {
        setProductoSeleccionado(prod);
        setRenovaciones([]);
        setRenovacionSeleccionada(null);
        // Limpiar selecciones de licencia si cambia el producto
        setTipoLicenciaSeleccionado(null);
        setLicenciaServidorVinculada(null);

        try {
            const response = await fetch(`/api/Suscripciones/GetRenovacionesByProducto?idProducto=${prod.idProducto}`, {
                method: 'GET',
                headers: {
                        'Authorization': 'Bearer ' + token,
                        'Content-Type': 'application/json'
                    }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.iResponseType === 1) {
                    const parsed = JSON.parse(data.detalles);
                    setRenovaciones(parsed.table2 || []);
                } else {

                    showErrorToast(data.vMessage);
                }
            } else {

                showErrorToast(response.status + ' ' + response.statusText);
            }
        } catch (error) {

            showErrorToast('Hubo un problema con la petición: ' + error.message);
        }
    };

    /**
     * 🔧 Determina si se debe mostrar el selector de licencia servidor
     */
    const debeVincularServidor = () => {
        if (!tipoLicenciaSeleccionado) return false;

        // Caja (2), App Comandas (4) siempre se vinculan, Nube (3) opcionalmente
        return tipoLicenciaSeleccionado.idCatTipoLicencia === 2 ||
            tipoLicenciaSeleccionado.idCatTipoLicencia === 4 ||
            tipoLicenciaSeleccionado.idCatTipoLicencia === 3;
    };

    /**
   * 🆕 Determina si un ModuloExtra puede vincularse opcionalmente
     */
    const puedeVincularModuloExtra = () => {
        // Los módulos extra pueden vincularse opcionalmente a un Servidor
        return productoSeleccionado && productoSeleccionado.vTipoProducto === 'ModuloExtra';
    };

    /**
     * 🆕 Valida que los campos requeridos estén completos
     */
    const validarFormulario = () => {
        if (!productoSeleccionado || !renovacionSeleccionada || !nombreSuscripcion.trim()) {
            return false;
        }

        // Si es producto tipo Licencia, validar campos adicionales
        if (productoSeleccionado.vTipoProducto === 'Licencia') {
            if (!tipoLicenciaSeleccionado || !vUUIDLicencia || !vUUIDEmpresa) {
                return false;
            }

            // 🆕 Validar días de periodo de prueba (debe ser >= 0)
            if (iDiasPeriodoPrueba < 0) {
                return false;
            }

            // Validar vLatLon SOLO si es tipo Servidor (idCatTipoLicencia === 1)
            if (tipoLicenciaSeleccionado.idCatTipoLicencia === 1) {
                if (!vLatLon.trim()) {
                    return false;
                }

                // Validar formato de LatLon
                const latLonPattern = /^[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?),\s*[-+]?(180(\.0+)?|((1[0-7]\d)|([1-9]?\d))(\.\d+)?)$/;
                if (!latLonPattern.test(vLatLon.trim())) {
                    return false;
                }
            }

            // Si es Caja (2) o App Comandas (4), DEBE tener licencia servidor vinculada
            if ((tipoLicenciaSeleccionado.idCatTipoLicencia === 2 ||
                tipoLicenciaSeleccionado.idCatTipoLicencia === 4) &&
                !licenciaServidorVinculada) {
                return false;
            }
        }

        return true;
    };

    const handleSeleccionar = async () => {
        if (!validarFormulario()) return;
        setCargando(true);

        // Construir objeto producto según el tipo
        const productoData = {
            idProducto: productoSeleccionado.idProducto,
            idProductoRenovaciones: renovacionSeleccionada.idProductoRenovaciones,
            dPrecioUnitario: renovacionSeleccionada.dPrecioUnitario,
            dTasaCuotaIVA: 0.16,
            vTipoFactorIVA: 'Tasa',
            dIVA: renovacionSeleccionada.dIVA,
            dTotal: renovacionSeleccionada.dTotal,
            vTipoRenovacion: renovacionSeleccionada.vTipoRenovacion
        };

        // 🔧 Si es producto tipo Licencia, agregar campos adicionales
        if (productoSeleccionado.vTipoProducto === 'Licencia') {
            productoData.idCatTipoLicencia = tipoLicenciaSeleccionado.idCatTipoLicencia;
            productoData.vUUIDLicencia = vUUIDLicencia;

            // 🆕 Para tipo Nube (3): vUUIDEmpresa vacío si no hay vinculación
            if (tipoLicenciaSeleccionado.idCatTipoLicencia === 3) {
                productoData.vUUIDEmpresa = licenciaServidorVinculada ? licenciaServidorVinculada.vUUIDEmpresa : '';
                console.log('🔵 Licencia Nube: vUUIDEmpresa será gestionado por el SP');
            } else {
                productoData.vUUIDEmpresa = vUUIDEmpresa;
            }

            productoData.vLatLon = vLatLon.trim() || ''; // Si está vacío, enviar string vacío

            // 🆕 MAPEO AUTOMÁTICO: Obtener idCatCicloFacturacion desde el tipo de renovación
            productoData.idCatCicloFacturacion = obtenerIdCicloFacturacion(renovacionSeleccionada.vTipoRenovacion);

            // 🆕 NUEVO: Agregar días de periodo de prueba
            productoData.iDiasPeriodoPrueba = parseInt(iDiasPeriodoPrueba) || 0;

            // 🆕 NUEVO: Si es Caja (2) o App Comandas (4), agregar vUUIDLicenciaServidor
            if ((tipoLicenciaSeleccionado.idCatTipoLicencia === 2 ||
                tipoLicenciaSeleccionado.idCatTipoLicencia === 4) &&
                licenciaServidorVinculada) {
                productoData.vUUIDLicenciaServidor = licenciaServidorVinculada.vUUIDLicencia;
            }

            console.log(`📋 Ciclo de facturación mapeado: ${renovacionSeleccionada.vTipoRenovacion} → ${productoData.idCatCicloFacturacion}`);
            console.log(`⏱️ Días de periodo de prueba: ${productoData.iDiasPeriodoPrueba}`);
            if (productoData.vUUIDLicenciaServidor) {
                console.log(`🔗 UUID Licencia Servidor vinculada: ${productoData.vUUIDLicenciaServidor}`);
            }
        }

        const body = {
            idCliente: idCliente,
            vNombreSuscripcion: nombreSuscripcion.trim(),
            idLicencia: licenciaServidorVinculada ? licenciaServidorVinculada.idLicencia : null,
            vTipoRenovacion: renovacionSeleccionada.vTipoRenovacion,
            productos: [productoData]
        };

        console.log('📤 Enviando datos de suscripción:', body);

        try {
            const response = await fetch('/api/Suscripciones/CrearNuevaSuscripcion', {
                method: 'POST',
                headers:
                    {
                        'Authorization': 'Bearer ' + token,
                        'Content-Type': 'application/json'
                    },
                body: JSON.stringify(body)
            });

            if (response.ok) {
                const data = await response.json();
                if (data.iResponseType === 1) {

                    showSuccessToast(data.vMessage);
                    if (onSuscripcionCreada) onSuscripcionCreada();
                    handleClose();
                } else {

                    showErrorToast(data.vMessage || 'Error al crear la suscripción');
                }
            } else {

                showErrorToast(response.status + ' ' + response.statusText);
            }
        } catch (error) {

            showErrorToast('Error al crear la suscripción: ' + error.message);
        } finally {
            setCargando(false);
        }
    };

    return (
        <Modal show={show} onHide={handleClose} size="lg" backdrop="static" centered>
            <Modal.Header>
                <Modal.Title>Nueva suscripción</Modal.Title>
                <FalconCloseButton className="btn btn-circle btn-sm transition-base p-0" onClick={handleClose} />
            </Modal.Header>

            <Modal.Body>
                {/* Selector de Moneda */}
                <div className="mb-3 p-3 bg-light rounded">
                    <div className="d-flex align-items-center justify-content-between">
                        <div className="btn-group" role="group">
                            <input
                                type="radio"
                                className="btn-check"
                                name="moneda"
                                id="radio-mxn"
                                value="MXN"
                                checked={monedaSeleccionada === 'MXN'}
                                onChange={(e) => {
                                    setMonedaSeleccionada(e.target.value);
                                    setProductoSeleccionado(null);
                                    setRenovaciones([]);
                                    setRenovacionSeleccionada(null);
                                }}
                            />
                            <label className="btn btn-outline-success btn-sm" htmlFor="radio-mxn">
                                MXN - Pesos Mexicanos
                            </label>

                            <input
                                type="radio"
                                className="btn-check"
                                name="moneda"
                                id="radio-usd"
                                value="USD"
                                checked={monedaSeleccionada === 'USD'}
                                onChange={(e) => {
                                    setMonedaSeleccionada(e.target.value);
                                    setProductoSeleccionado(null);
                                    setRenovaciones([]);
                                    setRenovacionSeleccionada(null);
                                }}
                            />
                            <label className="btn btn-outline-primary btn-sm" htmlFor="radio-usd">
                                USD - Dólares
                            </label>
                        </div>
                    </div>
                    <div className="small text-muted mt-2">
                        <FontAwesomeIcon icon="info-circle" className="me-1" />
                        Solo se mostrarán productos con precios en la moneda seleccionada
                    </div>
                </div>

                <div className="d-flex" style={{ minHeight: '400px' }}>
                    {/* Panel izquierdo: Lista de productos */}
                    <div style={{ width: '45%', borderRight: '1px solid #eee' }} className="pe-3">
                        <h6 className="fw-bold">Productos</h6>
                        <div className="mt-2" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                            {productosFiltrados.length === 0 ? (
                                <div className="text-center text-muted py-4">
                                    <div className="mb-2" style={{ fontSize: '2rem', opacity: 0.3 }}>??</div>
                                    <div>No hay productos disponibles en {monedaSeleccionada}</div>
                                </div>
                            ) : (
                                productosFiltrados.map((p) => (
                                    <div key={p.idProducto}
                                        className={`p-2 rounded-2 mb-2 ${productoSeleccionado?.idProducto === p.idProducto ? 'bg-primary bg-opacity-10 border border-primary border-opacity-25' : 'border'}`}
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => onSelectProducto(p)}
                                    >
                                        <div className="d-flex justify-content-between align-items-start">
                                            <div className="flex-grow-1">
                                                <div className="fw-600">{p.vNombreProducto}</div>
                                                <div className="small text-muted">{p.vCodigoBarras}</div>
                                                {p.vTipoProducto && (
                                                    <Badge bg={p.vTipoProducto === 'Licencia' ? 'info' : 'secondary'} className="mt-1">
                                                        {p.vTipoProducto}
                                                    </Badge>
                                                )}
                                            </div>
                                            <span className={`badge ${p.vMoneda === 'USD' ? 'bg-primary' : 'bg-success'}`}>
                                                {p.vMoneda}
                                            </span>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                    {/* Panel derecho: Detalles del producto seleccionado */}
                    <div className="flex-grow-1 ps-3">
                        {productoSeleccionado ? (
                            <div className="d-flex flex-column h-100">
                                <div className="mb-2">
                                    <h6 className="fw-bold mb-1">{productoSeleccionado.vNombreProducto}</h6>
                                    <div className="small text-muted">{productoSeleccionado.vCodigoBarras}</div>
                                </div>

                                {/* Nombre de la suscripción */}
                                <div className="mb-3">
                                    <Form.Group controlId="nombreSuscripcion">
                                        <Form.Label className="fw-bold">Nombre de la suscripción *</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={nombreSuscripcion}
                                            onChange={(e) => setNombreSuscripcion(e.target.value)}
                                            placeholder="Ej. Suscripción POS Servidor"
                                        />
                                    </Form.Group>
                                </div>

                                {/* 🔧 NUEVO: Si es producto tipo Licencia, mostrar campos adicionales */}
                                {productoSeleccionado.vTipoProducto === 'Licencia' && (
                                    <>
                                        {/* Selector de Tipo de Licencia */}
                                        <div className="mb-3">
                                            <Form.Group controlId="tipoLicencia">
                                                <Form.Label className="fw-bold">Tipo de Licencia *</Form.Label>
                                                <Form.Select
                                                    value={tipoLicenciaSeleccionado ? tipoLicenciaSeleccionado.idCatTipoLicencia : ''}
                                                    onChange={(e) => {
                                                        const tipo = tiposLicencia.find(t => t.idCatTipoLicencia.toString() === e.target.value);
                                                        setTipoLicenciaSeleccionado(tipo || null);
                                                        setLicenciaServidorVinculada(null); // Limpiar vinculación al cambiar tipo
                                                    }}
                                                >
                                                    <option value="">Selecciona un tipo de licencia</option>
                                                    {tiposLicencia.map((tipo) => (
                                                        <option key={tipo.idCatTipoLicencia} value={tipo.idCatTipoLicencia}>
                                                            {tipo.vDescripcionTipo}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </div>

                                        {/* ?? Selector de Licencia Servidor (condicional) */}
                                        {debeVincularServidor() && (
                                            <div className="mb-3">
                                                <Form.Group controlId="licenciaServidor">
                                                    <Form.Label className="fw-bold">
                                                        Licencia Servidor {tipoLicenciaSeleccionado?.idCatTipoLicencia === 4 ? '*' : '(Opcional)'}
                                                    </Form.Label>
                                                    {cargandoLicenciasServidor ? (
                                                        <div className="d-flex align-items-center">
                                                            <Spinner animation="border" size="sm" className="me-2" />
                                                            <span className="text-muted">Cargando licencias servidor...</span>
                                                        </div>
                                                    ) : licenciasServidor.length === 0 ? (
                                                        <Alert variant="warning" className="mb-2 py-2">
                                                            <FontAwesomeIcon icon="exclamation-triangle" className="me-2" />
                                                            No hay licencias tipo Servidor disponibles para este cliente
                                                        </Alert>
                                                    ) : (
                                                        <Form.Select
                                                            value={licenciaServidorVinculada ? licenciaServidorVinculada.idLicencia : ''}
                                                            onChange={(e) => {
                                                                const licencia = licenciasServidor.find(lic => lic.idLicencia.toString() === e.target.value);
                                                                setLicenciaServidorVinculada(licencia || null);
                                                            }}
                                                        >
                                                            <option value="">
                                                                {tipoLicenciaSeleccionado?.idCatTipoLicencia === 4
                                                                    ? 'Selecciona una licencia Servidor'
                                                                    : 'Sin vincular a Servidor'}
                                                            </option>
                                                            {licenciasServidor.map((licencia) => (
                                                                <option key={licencia.idLicencia} value={licencia.idLicencia}>
                                                                    ID: {licencia.idLicencia}
                                                                    {licencia.vUUIDEmpresa && ` - ${licencia.vUUIDEmpresa.substring(0, 8)}...`}
                                                                </option>
                                                            ))}
                                                        </Form.Select>
                                                    )}
                                                    <Form.Text className="text-muted">
                                                        {tipoLicenciaSeleccionado?.idCatTipoLicencia === 4
                                                            ? 'Las licencias Caja deben vincularse a una licencia Servidor'
                                                            : 'Opcional: vincula esta licencia a un Servidor existente'}
                                                    </Form.Text>
                                                </Form.Group>
                                            </div>
                                        )}

                                        {/* UUIDs generados con botones para regenerar */}
                                        {vUUIDLicencia && vUUIDEmpresa && (
                                            <div className="mb-3">
                                                <div className="border rounded p-3 bg-light">
                                                    <div className="mb-2">
                                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                                            <Form.Label className="mb-0 fw-bold small">UUID Licencia</Form.Label>
                                                            <Button
                                                                variant="falcon-primary"
                                                                size="sm"
                                                                onClick={regenerarUUIDLicencia}
                                                                className="py-0 px-2"
                                                            >
                                                                <FontAwesomeIcon icon="sync" className="me-1" />
                                                                Regenerar
                                                            </Button>
                                                        </div>
                                                        <Form.Control
                                                            type="text"
                                                            value={vUUIDLicencia}
                                                            readOnly
                                                            className="font-monospace small"
                                                        />
                                                    </div>

                                                    {/* 🆕 Solo mostrar UUID Empresa si NO es tipo Nube */}
                                                    {tipoLicenciaSeleccionado?.idCatTipoLicencia !== 3 && (
                                                        <div>
                                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                                <Form.Label className="mb-0 fw-bold small">UUID Empresa</Form.Label>
                                                                <Button
                                                                    variant="falcon-primary"
                                                                    size="sm"
                                                                    onClick={regenerarUUIDEmpresa}
                                                                    className="py-0 px-2"
                                                                >
                                                                    <FontAwesomeIcon icon="sync" className="me-1" />
                                                                    Regenerar
                                                                </Button>
                                                            </div>
                                                            <Form.Control
                                                                type="text"
                                                                value={vUUIDEmpresa}
                                                                readOnly
                                                                className="font-monospace small"
                                                            />
                                                        </div>
                                                    )}

                                                    {/* 🆕 Mensaje informativo para tipo Nube */}
                                                    {tipoLicenciaSeleccionado?.idCatTipoLicencia === 3 && (
                                                        <Alert variant="info" className="py-2 px-3 mb-0 mt-2">
                                                            <FontAwesomeIcon icon="info-circle" className="me-2" />
                                                            <small>
                                                                {licenciaServidorVinculada
                                                                    ? 'El UUID Empresa se tomará de la licencia Servidor vinculada'
                                                                    : 'El UUID Empresa quedará vacío (sin vinculación a Servidor)'}
                                                            </small>
                                                        </Alert>
                                                    )}

                                                    <Form.Text className="text-muted d-block mt-2">
                                                        <FontAwesomeIcon icon="info-circle" className="me-1" />
                                                        Los UUIDs se generan automáticamente. Puedes regenerarlos si es necesario.
                                                    </Form.Text>
                                                </div>
                                            </div>
                                        )}

                                        {/* 🆕 NUEVO: Campo de Días de Periodo de Prueba */}
                                        <div className="mb-3">
                                            <Form.Group controlId="iDiasPeriodoPrueba">
                                                <Form.Label className="fw-bold">
                                                    Días de Periodo de Prueba *
                                                </Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    min="0"
                                                    max="365"
                                                    value={iDiasPeriodoPrueba}
                                                    onChange={(e) => setIDiasPeriodoPrueba(e.target.value)}
                                                    placeholder="Ej. 15"
                                                />
                                                <Form.Text className="text-muted">
                                                    <FontAwesomeIcon icon="info-circle" className="me-1" />
                                                    Número de días que el cliente podrá usar la licencia sin costo (0 para sin periodo de prueba)
                                                </Form.Text>
                                            </Form.Group>
                                        </div>

                                        {/* 🆕 Campo de Latitud, Longitud (obligatorio SOLO para Servidor) */}
                                        {tipoLicenciaSeleccionado && tipoLicenciaSeleccionado.idCatTipoLicencia === 1 && (
                                            <div className="mb-3">
                                                <Form.Group controlId="vLatLon">
                                                    <Form.Label className="fw-bold">
                                                        Latitud, Longitud *
                                                        <a href="https://www.google.com.mx/maps" rel="noreferrer" target="_blank" className="ms-2 small">
                                                            <FontAwesomeIcon icon="map-marker-alt" className="me-1" />
                                                            Abrir Google Maps
                                                        </a>
                                                    </Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        value={vLatLon}
                                                        onChange={(e) => setVLatLon(e.target.value)}
                                                        placeholder="Ej. 19.432608,-99.133209"
                                                        isInvalid={vLatLon.trim() !== '' && !/^[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?),\s*[-+]?(180(\.0+)?|((1[0-7]\d)|([1-9]?\d))(\.\d+)?)$/.test(vLatLon.trim())}
                                                        autoComplete="off"
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        Formato inválido. Use: latitud,longitud (Ej. 19.432608,-99.133209)
                                                    </Form.Control.Feedback>
                                                    <Form.Text className="text-muted">
                                                        <FontAwesomeIcon icon="info-circle" className="me-1" />
                                                        Formato: latitud,longitud. Puedes obtenerlo desde Google Maps haciendo clic derecho en el mapa.
                                                    </Form.Text>
                                                </Form.Group>
                                            </div>
                                        )}
                                    </>
                                )}

                                {/* 🆕 Selector de Licencia Servidor para ModuloExtra (OPCIONAL) */}
                                {puedeVincularModuloExtra() && (
                                    <div className="mb-3">
                                        <Form.Group controlId="licenciaServidorModulo">
                                            <Form.Label className="fw-bold">
                                                Vincular a Licencia Servidor (Opcional)
                                            </Form.Label>
                                            {cargandoLicenciasServidor ? (
                                                <div className="d-flex align-items-center">
                                                    <Spinner animation="border" size="sm" className="me-2" />
                                                    <span className="text-muted">Cargando licencias servidor...</span>
                                                </div>
                                            ) : licenciasServidor.length === 0 ? (
                                                <Alert variant="info" className="mb-2 py-2">
                                                    <FontAwesomeIcon icon="info-circle" className="me-2" />
                                                    No hay licencias tipo Servidor disponibles para este cliente
                                                </Alert>
                                            ) : (
                                                <Form.Select
                                                    value={licenciaServidorVinculada ? licenciaServidorVinculada.idLicencia : ''}
                                                    onChange={(e) => {
                                                        const licencia = licenciasServidor.find(lic => lic.idLicencia.toString() === e.target.value);
                                                        setLicenciaServidorVinculada(licencia || null);
                                                    }}
                                                >
                                                    <option value="">Sin vincular a Servidor</option>
                                                    {licenciasServidor.map((licencia) => (
                                                        <option key={licencia.idLicencia} value={licencia.idLicencia}>
                                                            ID: {licencia.idLicencia}
                                                            {licencia.vUUIDEmpresa && ` - ${licencia.vUUIDEmpresa.substring(0, 8)}...`}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            )}
                                            <Form.Text className="text-muted">
                                                Opcional: vincula este módulo a una licencia Servidor existente del cliente
                                            </Form.Text>
                                        </Form.Group>
                                    </div>
                                )}

                                {/* Tipos de renovación */}
                                <div className="flex-grow-1">
                                    <h6 className="fw-bold">Tipos de renovación *</h6>
                                    <div className="mt-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                        {renovaciones.length === 0 ? (
                                            <div className="text-muted small">Selecciona un producto para ver renovaciones disponibles</div>
                                        ) : (
                                            renovaciones.map(r => (
                                                <div key={r.idProductoRenovaciones}
                                                    className={`p-2 rounded-2 mb-2 d-flex justify-content-between align-items-center ${renovacionSeleccionada?.idProductoRenovaciones === r.idProductoRenovaciones ? 'bg-success bg-opacity-10 border border-success border-opacity-25' : 'border'}`}
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => setRenovacionSeleccionada(r)}
                                                >
                                                    <div>
                                                        <div className="fw-600 text-capitalize">{r.vTipoRenovacion}</div>
                                                        <div className="small text-muted">
                                                            Precio: ${parseFloat(r.dPrecioUnitario).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                                                        </div>
                                                    </div>
                                                    <div className="text-end">
                                                        <div className="fw-bold text-success">
                                                            ${parseFloat(r.dTotal).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                                                        </div>
                                                        <div className="small text-muted">+ IVA</div>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>

                                {/* Botón crear */}
                                <div className="text-center mt-3">
                                    <Button
                                        onClick={handleSeleccionar}
                                        variant='success'
                                        size="md"
                                        className="px-4"
                                        disabled={!validarFormulario() || cargando}
                                    >
                                        {cargando ? (
                                            <>
                                                <Spinner animation="border" size="sm" className="me-2" />
                                                Creando...
                                            </>
                                        ) : (
                                            <>
                                                <FontAwesomeIcon icon="check" className="me-2" />
                                                Crear Suscripción
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="flex-grow-1 d-flex align-items-center justify-content-center">
                                <div className="text-center text-muted">
                                    <div style={{ fontSize: '3rem', opacity: 0.3 }}>👈</div>
                                    <div>Selecciona un producto de la lista</div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </Modal.Body>

            <Modal.Footer className="pt-2 justify-content-between">
                <Button onClick={handleClose} variant='secondary' size="sm">
                    <FontAwesomeIcon icon="times" className="me-1" />
                    Cancelar
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

ModalNuevaSuscripcion.propTypes = {
    show: PropTypes.bool.isRequired,
    setShow: PropTypes.func.isRequired,
    idCliente: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onSuscripcionCreada: PropTypes.func
};

export default ModalNuevaSuscripcion;
